import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  PlacementStyles,
  AlloyPlacementStyles,
  Places,
  Themes,
  Sizes,
  TriggerTargets
} from './helpers/consts';
import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';
import {
  ThemeConsumer,
  Themes as AlloyOrMatrixThemes
} from '@adsk/bim360-matrix-react-helpers/es/theme';

/**
 * @class OverlayInnerChild - inner class of the `OverlayWrapper` content
 **/
class OverlayInnerChild extends Component {
  constructor(props) {
    super(props);
    this.state = {
      arrowStyle: null
    };
    this.popoverDomRef = null;
    this.getPluckedTooltipStyle = this.getPluckedTooltipStyle.bind(this);
    this.getRef = this.getRef.bind(this);
    this.updateStyleIfNecessary = this.updateStyleIfNecessary.bind(this);
  }

  componentDidMount() {
    this.updateStyleIfNecessary();
  }

  componentDidUpdate() {
    this.updateStyleIfNecessary();
  }

  /**
   * called after update if changes are required in the style
   */
  updateStyleIfNecessary() {
    if (this.props.matchArrowBackground && this.popoverDomRef) {
      const backgroundColor = window.getComputedStyle(this.popoverDomRef).backgroundColor;

      if (!this.state.arrowStyle || backgroundColor !== this.state.arrowStyle.borderColor) {
        const arrowStyle = {
          borderColor: backgroundColor
        };
        this.setState({ arrowStyle });
      }
    }
  }

  /**
   * get the style object for the tooltip element from the tooltip style (without the padding and margin attributes)
   */

  getPluckedTooltipStyle(tooltipStyle = {}) {
    const newStyle = Object.keys(tooltipStyle).reduce((object, key) => {
      if (/^(?!margin|padding|border)/i.test(key)) {
        object[key] = tooltipStyle[key];
      }
      return object;
    }, {});
    return newStyle;
  }

  getRef(ref) {
    this.popoverDomRef = ref;
    this.props.overlayRef(ref);
  }

  render() {
    const {
      style,
      triggerTarget,
      place,
      showArrow,
      arrowProps,
      tooltipClassName,
      theme,
      size,
      content,
      tooltipStyle
    } = this.props;
    const { arrowStyle } = this.state;
    const { style: arrowPropsStyle, ...restArrowProps } = arrowProps;
    const basePlacement = place.split('-')[0];
    return (
      <ThemeConsumer>
        {({ theme: alloyOrMatrixTheme }) => {
          const placementStylesObj =
            alloyOrMatrixTheme === AlloyOrMatrixThemes.ALLOY
              ? AlloyPlacementStyles
              : PlacementStyles;
          const placementStyle = placementStylesObj[basePlacement] || {};
          const containerClasses = classnames(
            `Tooltip__container Tooltip__container--${basePlacement} Tooltip__container--${theme} Tooltip__container--${alloyOrMatrixTheme} Tooltip__container--${size}`,
            { 'Tooltip__container--show-on-hover': triggerTarget !== TriggerTargets.CHILDREN },
            tooltipClassName
          );
          return (
            <div
              ref={this.getRef}
              data-testid="Tooltip__container"
              className={containerClasses}
              style={{
                ...this.getPluckedTooltipStyle(tooltipStyle),
                ...style,
                ...placementStyle.element
              }}
            >
              {showArrow && (
                <div
                  {...restArrowProps}
                  className={classnames(
                    'Tooltip__container-arrow',
                    `Tooltip__container-arrow--${place}`
                  )}
                  style={{
                    ...placementStyle.arrow,
                    ...arrowStyle,
                    ...arrowPropsStyle
                  }}
                />
              )}
              {content}
            </div>
          );
        }}
      </ThemeConsumer>
    );
  }
}

OverlayInnerChild.propTypes = {
  /**
   * The contents to show within the popover.
   */
  content: PropTypes.node.isRequired,
  /**
   * className to add to the popover container - deprecate?
   */
  tooltipClassName: PropTypes.string,
  /**
   * style to add to the popover container - deprecate?
   */
  tooltipStyle: PropTypes.object,
  /**
   * style to add to the layout container wrapping the children and the tooltip
   */
  style: PropTypes.object,
  /**
   * A Node, Component instance, or function that returns either. The `container` will have the Tooltip appended to it.
   */
  container: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /**
   * Tooltip place
   */
  place: PropTypes.oneOf(Object.values(Places)),
  /**
   * Specify whether the overlay arrow color should match the popoverClassName and tooltipStyle background
   */
  matchArrowBackground: PropTypes.bool,
  /**
   * The coloring scheme of the overlay
   */
  theme: PropTypes.oneOf(objectValues(Themes)),
  /**
   * The size scheme of the tooltip
   */
  size: PropTypes.oneOf(objectValues(Sizes)),
  /**
   * whether to not have the arrow - show and default to true
   */
  showArrow: PropTypes.bool,
  /**
   * The Overlay target (either the children or the tooltip itself)
   */
  triggerTarget: PropTypes.oneOf(objectValues(TriggerTargets)),
  /**
   * Ref function for the overlay container
   */
  overlayRef: PropTypes.func,
  /**
   * arrow style and additional properties from Overlay
   */
  arrowProps: PropTypes.object
};

export default OverlayInnerChild;
